import { MouseEvent as ReactMouseEvent } from 'react';
import {
	Elements,
	OnLoadParams,
	Connection,
	Edge,
	Node,
	isNode,
	isEdge,
	removeElements,
	XYPosition,
} from 'react-flow-renderer';
import { useDispatch } from 'react-redux';
import { NODE_TYPES, NodePositionParams, ReactFlowSchema } from '@Types';
import { setNodePosition, setStartNodeId } from '@Actions';
import { getStartNodeId, getNodes } from '@Utils/TreeUtils';
import {
	duplicateNode,
	validateConnection,
	createConnectionEdge,
} from './decisionTreeHelpers';
import { buildNodeData, NodeHandlers } from './buildNodeData';

const nodeWidth = 200;
const nodeHeight = 128;

export interface UseTreeElementsArgs {
	reactFlowInstance: OnLoadParams | null;
	elements: Elements;
	setElements: React.Dispatch<React.SetStateAction<Elements>>;
	setIsDirty: (dirty: boolean) => void;
	currentTreeStartNodeId: string;
	shouldEnableMultipleInputs: string | Object;
	onEditClick: (nodeId: string) => void;
	isRehabTreeEditable: boolean;
	setStartNodeConnectedLimit: (limit: boolean) => void;
	setNodeIdToEdit: (id: string) => void;
}

export interface UseTreeElementsResult {
	addToTree: (values: ReactFlowSchema) => void;
	updateInTree: (valuesArray: ReactFlowSchema[]) => void;
	onConnect: (params: Connection | Edge) => void;
	handleElementsRemove: (elementsToRemove: Elements) => void;
	handleNodeDragStop: (event: ReactMouseEvent, node: Node) => void;
	nodeHandlers: NodeHandlers;
}

export const useTreeElements = ({
	reactFlowInstance,
	elements,
	setElements,
	setIsDirty,
	currentTreeStartNodeId,
	shouldEnableMultipleInputs,
	onEditClick,
	isRehabTreeEditable,
	setStartNodeConnectedLimit,
	setNodeIdToEdit,
}: UseTreeElementsArgs): UseTreeElementsResult => {
	const dispatch = useDispatch();

	const handleNodeDuplicateClick = (nodeId: string) => {
		setElements(ele => {
			const newNode = duplicateNode(nodeId, ele);
			if (newNode) {
				setIsDirty(true);
				return [...ele, newNode];
			}
			return ele;
		});
	};

	const handleNodeDeleteClick = (nodeId: string) => {
		setElements(els => {
			const elementToRemove = els.find(el => el.id === nodeId);
			if (elementToRemove) {
				const allNodes = getNodes(els);
				if (allNodes.length === 2) {
					const startNodeId = getStartNodeId(els);
					const startElement = els.find(el => el.id === startNodeId);

					if (startElement)
						return removeElements(
							[startElement, elementToRemove],
							els,
						);
				}
				return removeElements([elementToRemove], els);
			} else {
				return els;
			}
		});
		setIsDirty(true);
	};

	const nodeHandlers: NodeHandlers = {
		onEditClick: onEditClick,
		onDeleteClick: handleNodeDeleteClick,
		onDuplicateClick: handleNodeDuplicateClick,
	};

	const getUpdatedElements = (
		currentElements: Elements,
		formValues: ReactFlowSchema,
		nodeId?: number | string | null,
	): Elements => {
		const currentNodes = currentElements.filter(isNode);
		const currentEdges = currentElements.filter(isEdge);
		let updatedNodes: (Node | Edge)[] = [];
		if (nodeId) {
			updatedNodes = currentNodes?.map(node => {
				const updatedNode: Node =
					node.id === nodeId
						? {
								...node,
								data: {
									...node.data,
									...formValues.data,
									title: formValues.label,
								},
							}
						: node;
				return updatedNode;
			});
		} else {
			// create a new node
			const getNewNodePosition = (): XYPosition => {
				const initialX = 24;
				const initialY = 24;
				if (!reactFlowInstance) {
					return { x: initialX, y: initialY };
				}
				const maxNodesPerRow = 5;
				const maxRowsToCheck = 5;

				let currentRowY = initialY;
				for (
					let currentRow = 0;
					currentRow < maxRowsToCheck;
					currentRow++
				) {
					let currentRowXs: number[] = [];
					if (currentElements) {
						currentRowXs = currentElements
							.filter(
								element =>
									isNode(element) &&
									element.position.y === currentRowY,
							)
							.map(element => (element as Node).position.x)
							.sort((n1, n2) => n1 - n2);
					}
					if (currentRowXs.length === 0) {
						return { x: initialX, y: currentRowY };
					} else if (currentRowXs.length < maxNodesPerRow) {
						return {
							x: currentRowXs.at(-1)! + nodeWidth + 36,
							y: currentRowY,
						};
					}
					currentRowY += nodeHeight + 24;
				}
				return { x: initialX, y: initialY };
			};
			const nodeId = 'idx_' + Date.now().toString();
			const newNode: Node = {
				id: nodeId,
				type: formValues.type ? formValues.type : NODE_TYPES.query,
				position: getNewNodePosition(),
				data: buildNodeData(
					nodeId,
					formValues.label,
					formValues.data?.nodeData,
					nodeHandlers,
					isRehabTreeEditable,
				),
			};
			updatedNodes = [...currentNodes, newNode];
		}

		updatedNodes = [...updatedNodes, ...currentEdges];

		return updatedNodes;
	};

	const updateInTree = (valuesArray: ReactFlowSchema[]) => {
		if (!reactFlowInstance) {
			return;
		}
		if (valuesArray.length === 0) {
			return;
		}
		setIsDirty(true);
		let currentElements = elements ?? reactFlowInstance.getElements();
		valuesArray.forEach(values => {
			currentElements = getUpdatedElements(
				currentElements,
				values,
				values.id,
			);
		});
		setElements(currentElements);
	};

	const addToTree = (values: ReactFlowSchema) => {
		if (!reactFlowInstance) {
			getUpdatedElements([], values);
			return;
		}
		setTimeout(() => {
			setNodeIdToEdit('');
		}, 1000);
		setIsDirty(true);
		const newElements = getUpdatedElements(
			reactFlowInstance.getElements(),
			values,
			values.id,
		);

		const startNodeId = getStartNodeId(newElements);
		if (newElements.length === 1 && !startNodeId) {
			const startNode = {
				id: 'idxStart_' + Date.now().toString(),
				// Cant translate as the label is used for code :(
				data: { label: 'Start' },
				style: {
					border: '1px solid #5F60FF',
					padding: 10,
					background: '#5F60FF',
					borderRadius: '1rem',
					color: 'white',
					fontSize: '1.2rem',
				},
				type: 'input',
				position: { x: 700, y: 50 },
			};
			dispatch(setStartNodeId(startNode.id));
			newElements.push(startNode);
		}
		setElements(newElements);
	};

	const isLoop = (
		sourceId: string | null,
		targetId: string | null,
		allNodes: Node[],
		allEdges: Edge[],
	): boolean => {
		if (sourceId == null || targetId == null) return false;

		const connectEdges = allEdges.filter(edge => edge.source == targetId);
		if (connectEdges.length === 0) return false;

		// Direct loop: an outgoing edge from target points back to source
		if (connectEdges.some(edge => edge.target == sourceId)) return true;

		// Recursive: follow each outgoing edge to check for indirect loops
		return connectEdges.some(edge =>
			isLoop(sourceId, edge.target, allNodes, allEdges),
		);
	};

	const onConnect = (params: Connection | Edge) => {
		const validation = validateConnection(
			params,
			elements,
			currentTreeStartNodeId,
			shouldEnableMultipleInputs,
			isLoop,
		);

		if (validation.blocked) {
			if (validation.reason === 'start-node-limit') {
				setStartNodeConnectedLimit(true);
			} else {
				setStartNodeConnectedLimit(false);
			}
			return;
		}
		setStartNodeConnectedLimit(false);

		const newConnection = createConnectionEdge(params, elements);
		if (!newConnection) return;

		setElements(elements => elements.concat(newConnection));
		setIsDirty(true);
	};

	const handleElementsRemove = (elementsToRemove: Elements) => {
		setElements(els => removeElements(elementsToRemove, els));
	};

	const handleNodeDragStop = (event: ReactMouseEvent, node: Node) => {
		const posParams: NodePositionParams = {
			Id: node.id,
			position: node.position,
		};
		dispatch(setNodePosition(posParams));
		setIsDirty(true);
	};

	return {
		addToTree,
		updateInTree,
		onConnect,
		handleElementsRemove,
		handleNodeDragStop,
		nodeHandlers,
	};
};
